import type { Department } from '@opti-sea/core';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useSession } from './auth';
import { unwrap, useDb, type Db } from './supabase';

/**
 * What the signed-in office user may see (PLAN §2.3): Admin (all companies) or DPA of one or more
 * companies. Masters and officers use the phone (dashboard access for Masters: Backlog).
 */
export interface Company {
  id: string;
  name: string;
  shortName: string | null;
}

export interface Vessel {
  id: string;
  companyId: string;
  name: string;
  imo: string;
  vesselType: string | null;
  flag: string | null;
}

export interface Position {
  code: string;
  department: Department;
  sortOrder: number;
}

export interface Access {
  userId: string;
  email: string;
  fullName: string;
  isAdmin: boolean;
  dpaCompanyIds: string[];
  companies: Company[];
  vessels: Vessel[];
  positions: Position[];
}

export async function loadAccess(db: Db, userId: string): Promise<Access> {
  const [profile, admins, members, companies, vessels, positions] = await Promise.all([
    db.from('profiles').select('full_name, email').eq('id', userId).maybeSingle(),
    db.from('platform_admins').select('user_id').eq('user_id', userId),
    db
      .from('company_members')
      .select('company_id')
      .eq('user_id', userId)
      .eq('role', 'dpa')
      .is('revoked_at', null),
    db.from('companies').select('id, name, short_name').is('archived_at', null).order('name'),
    db
      .from('vessels')
      .select('id, company_id, name, imo, vessel_type, flag')
      .is('archived_at', null)
      .order('name'),
    db.from('positions').select('code, department, sort_order').order('sort_order'),
  ]);
  return {
    userId,
    email: profile.data?.email ?? '',
    fullName: profile.data?.full_name ?? '',
    isAdmin: unwrap(admins).length > 0,
    dpaCompanyIds: unwrap(members).map((m) => m.company_id),
    companies: unwrap(companies).map((c) => ({ id: c.id, name: c.name, shortName: c.short_name })),
    vessels: unwrap(vessels).map((v) => ({
      id: v.id,
      companyId: v.company_id,
      name: v.name,
      imo: v.imo,
      vesselType: v.vessel_type,
      flag: v.flag,
    })),
    positions: unwrap(positions).map((p) => ({
      code: p.code,
      department: p.department,
      sortOrder: p.sort_order,
    })),
  };
}

export function hasOfficeAccess(access: Access): boolean {
  return access.isAdmin || access.dpaCompanyIds.length > 0;
}

/** Companies the user works for: all for Admin, own companies for a DPA. */
export function officeCompanies(access: Access): Company[] {
  return access.isAdmin
    ? access.companies
    : access.companies.filter((c) => access.dpaCompanyIds.includes(c.id));
}

export function useAccessQuery() {
  const db = useDb();
  const session = useSession();
  const userId = session?.user.id ?? null;
  return useQuery({
    queryKey: ['access', userId],
    queryFn: () => loadAccess(db, userId ?? ''),
    enabled: userId !== null,
  });
}

interface Workplace {
  access: Access;
  company: Company;
  vessels: Vessel[];
  chooseCompany: (id: string) => void;
}

const WorkplaceContext = createContext<Workplace | null>(null);

const COMPANY_KEY = 'opti-sea.company';

function rememberedCompany(): string | null {
  try {
    return window.localStorage.getItem(COMPANY_KEY);
  } catch {
    return null;
  }
}

/** The company being looked at (remembered in this browser) and its vessels. */
export function WorkplaceProvider({ access, children }: { access: Access; children: ReactNode }) {
  const companies = officeCompanies(access);
  const [chosen, setChosen] = useState<string | null>(rememberedCompany);
  const company = companies.find((c) => c.id === chosen) ?? companies[0];
  const value: Workplace | null =
    company === undefined
      ? null
      : {
          access,
          company,
          vessels: access.vessels.filter((v) => v.companyId === company.id),
          chooseCompany: (id: string) => {
            setChosen(id);
            try {
              window.localStorage.setItem(COMPANY_KEY, id);
            } catch {
              // Private window: the choice lasts until the page is closed.
            }
          },
        };
  return <WorkplaceContext.Provider value={value}>{children}</WorkplaceContext.Provider>;
}

export function useWorkplace(): Workplace {
  const value = useContext(WorkplaceContext);
  if (value === null) {
    throw new Error('WorkplaceContext is missing');
  }
  return value;
}

export function useOptionalWorkplace(): Workplace | null {
  return useContext(WorkplaceContext);
}
